/**
 * ADR-0255 §11 — ops2026 Protocol Test Agents view.
 *
 * Pick a site + a protocol, click Run, see the full per-step detail
 * inline: method, URL, request headers (redacted), request body,
 * response status, response headers, response body preview,
 * tri-state outcome (pass / fail / n_a), latency, record_id +
 * inbound_protocol. Each step has a "Copy as curl" button that
 * rebuilds the request from captured + REDACTED material; the curl is
 * for shape inspection only — redacted auth headers are not replayable.
 *
 * Visual tokens come from `useC` (same theme the smoke view uses):
 * mint for pass, coral for fail, amber for n_a.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AdminProtocolTestProtocol,
  AdminProtocolTestRunDetail,
  AdminProtocolTestRunHeader,
  AdminProtocolTestStep,
  AdminProtocolTestStepOutcome,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { formatIsoCompact, formatRelativeShort } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

const PROTOCOLS: readonly AdminProtocolTestProtocol[] = [
  'all',
  'discovery',
  'mcp',
  'a2a',
  'ucp',
  'pap',
  'ap2',
  'web_bot_auth',
];

function outcomeTone(outcome: AdminProtocolTestStepOutcome): PillTone {
  if (outcome === 'pass') return 'mint';
  if (outcome === 'n_a') return 'amber';
  return 'coral';
}

function outcomeLabel(outcome: AdminProtocolTestStepOutcome): string {
  if (outcome === 'pass') return 'Pass';
  if (outcome === 'n_a') return 'N/A';
  return 'Fail';
}

function statusTone(header: AdminProtocolTestRunHeader): PillTone {
  if (header.status === 'running') return 'sky';
  if (header.status === 'completed') return 'mint';
  if (header.status === 'refused') return 'amber';
  return 'coral';
}

const ULID_RE = /^[0-9A-HJ-NP-TV-Z]{26}$/i;

const ERROR_COPY: Record<string, string> = {
  invalid_domain: 'That does not look like a domain. Try example.com.',
  invalid_site_id: 'Enter a domain (example.com) or a site ID.',
  site_not_found: 'No Sill site is registered for that domain.',
  site_key_missing: 'This site has no site key yet, so it cannot be tested. Finish install first.',
  site_rate_limited: 'This site has had 12 test runs in the last hour. Try again later.',
  rate_limited: 'You have started too many runs. Wait a few minutes and try again.',
  domain_ambiguous: 'Several sites use that domain. Pick one below.',
  invalid_protocol: 'Pick a protocol.',
};

function errorCode(err: unknown): string | null {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | undefined;
    if (body !== undefined && typeof body.error === 'string') return body.error;
  }
  return null;
}

function errorMessage(err: unknown): string {
  const code = errorCode(err);
  if (code !== null) return ERROR_COPY[code] ?? code;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Only admins can start runs. Support staff can view runs.';
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

interface Candidate { readonly site_id: string; readonly domain: string; readonly status: string }

/**
 * Rebuild a shell curl command from the captured + REDACTED step
 * material. Secret-bearing header values are already replaced with
 * `<redacted:len=N>` by the API (see `redact.ts`); we keep them
 * verbatim in the curl so a reader can see the shape without a
 * replayable credential.
 */
function stepAsCurl(step: AdminProtocolTestStep): string {
  const parts: string[] = [`curl -i -X ${step.method} \\\n  '${step.url}'`];
  const headers = step.request_headers_json ?? {};
  for (const [name, value] of Object.entries(headers)) {
    const safe_value = value.replace(/'/g, "'\\''");
    parts.push(`  -H '${name}: ${safe_value}'`);
  }
  if (step.request_body_preview !== null && step.request_body_preview.length > 0) {
    const safe_body = step.request_body_preview.replace(/'/g, "'\\''");
    parts.push(`  --data-raw '${safe_body}'`);
  }
  const header_comment = Object.values(headers).some((v) => v.startsWith('<redacted:'))
    ? '# NOTE: <redacted:*> header values are placeholders — NOT replayable credentials.\n'
    : '';
  return header_comment + parts.join(' \\\n');
}

interface Props {
  readonly initialRunId?: string | null;
  readonly initialSiteId?: string | null;
}

export function ProtocolTestsView({ initialRunId = null, initialSiteId = null }: Props): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [siteId, setSiteId] = useState(initialSiteId ?? '');
  const [candidates, setCandidates] = useState<readonly Candidate[]>([]);
  const [protocol, setProtocol] = useState<AdminProtocolTestProtocol>('all');
  const [cartItemId, setCartItemId] = useState('');
  const [runs, setRuns] = useState<readonly AdminProtocolTestRunHeader[]>([]);
  const [current, setCurrent] = useState<AdminProtocolTestRunDetail | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [copyFlashId, setCopyFlashId] = useState<string | null>(null);

  const fetchRuns = useCallback(async (): Promise<void> => {
    try {
      const res = await api.listProtocolTests();
      setRuns(res.runs);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [api]);

  useEffect(() => { void fetchRuns(); }, [fetchRuns]);

  useEffect(() => {
    if (initialRunId !== null) {
      void (async (): Promise<void> => {
        try {
          const detail = await api.getProtocolTest(initialRunId);
          setCurrent(detail);
        } catch (err) {
          setError(errorMessage(err));
        }
      })();
    }
  }, [api, initialRunId]);

  const openRun = useCallback(async (runId: string): Promise<void> => {
    try {
      const detail = await api.getProtocolTest(runId);
      setCurrent(detail);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [api]);

  const run = async (forcedSiteId?: string): Promise<void> => {
    const target = (forcedSiteId ?? siteId).trim();
    if (target.length === 0) {
      setError('Enter a domain, for example richardfallah.com.');
      return;
    }
    setRunning(true);
    setError(null);
    setCandidates([]);
    try {
      const detail = await api.startProtocolTest({
        ...(ULID_RE.test(target) ? { site_id: target.toUpperCase() } : { domain: target }),
        protocol,
        ...(cartItemId.trim().length > 0 ? { cart_item_id: cartItemId.trim() } : {}),
      });
      setCurrent(detail);
      void fetchRuns();
    } catch (err) {
      setError(errorMessage(err));
      if (errorCode(err) === 'domain_ambiguous') {
        const body = (err as ApiError).body as { candidates?: Candidate[] } | undefined;
        setCandidates(body?.candidates ?? []);
      }
    } finally {
      setRunning(false);
    }
  };

  const copyCurl = async (step: AdminProtocolTestStep): Promise<void> => {
    try {
      await navigator.clipboard.writeText(stepAsCurl(step));
      setCopyFlashId(step.step_id);
      window.setTimeout(() => setCopyFlashId((prev) => prev === step.step_id ? null : prev), 1_500);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const payHeldOrder = async (): Promise<void> => {
    const run_id = current?.header.run_id;
    if (run_id === undefined) return;
    setRunning(true);
    setError(null);
    try {
      const detail = await api.payHeldOrder(run_id);
      setCurrent(detail);
      void fetchRuns();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const header = current?.header ?? null;
  const steps = current?.steps ?? [];
  const summary = useMemo(() => header?.summary ?? null, [header]);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
        OPS / PROTOCOL TEST AGENTS
      </div>
      <h1 style={{ fontFamily: serifFont, fontSize: 48, color: C.text, marginTop: 8, marginBottom: 20 }}>
        Protocol test agents
      </h1>
      <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginBottom: 24, maxWidth: 720 }}>
        Type a site's domain, pick a protocol and run a signed Sill test agent against it. Every step shows the request and response.
        In Stripe test mode, UCP + AP2 place a real test checkout you can see in Stripe test and in Held orders. Live mode never charges &mdash; UCP cancels, AP2 is skipped. No place_order, no refund. ADR-0255.
      </p>

      <Card>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            Site (domain or site ID)
            <input
              type="text"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !running) void run(); }}
              placeholder="richardfallah.com"
              autoComplete="off"
              spellCheck={false}
              style={{
                display: 'block', width: '100%', marginTop: 4, padding: '8px 10px',
                fontFamily: monoFont, fontSize: 13,
                background: C.surface2, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 2,
              }}
            />
          </label>
          <label style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            Protocol
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {PROTOCOLS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProtocol(p)}
                  style={{
                    fontFamily: monoFont, fontSize: 11,
                    padding: '6px 10px',
                    background: protocol === p ? C.lime : C.surface2,
                    color: protocol === p ? C.ink : C.body,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </label>
          {(protocol === 'ucp' || protocol === 'all') && (
            <label style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
              UCP cart item.id (optional)
              <input
                type="text"
                value={cartItemId}
                onChange={(e) => setCartItemId(e.target.value)}
                placeholder="(server picks cheapest in-stock)"
                style={{
                  display: 'block', width: '100%', marginTop: 4, padding: '8px 10px',
                  fontFamily: monoFont, fontSize: 13,
                  background: C.surface2, color: C.text,
                  border: `1px solid ${C.border}`, borderRadius: 2,
                }}
              />
            </label>
          )}
          <div>
            <button
              type="button"
              onClick={() => void run()}
              disabled={running || siteId.trim().length === 0}
              style={{
                fontFamily: sansFont, fontSize: 13, fontWeight: 500,
                padding: '10px 18px',
                background: C.lime, color: C.ink,
                border: `1px solid ${C.border}`, borderRadius: 2,
                cursor: running ? 'wait' : 'pointer',
                opacity: running ? 0.6 : 1,
              }}
            >
              {running ? 'Running…' : 'Run'}
            </button>
          </div>
          {error !== null && (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
          )}
          {candidates.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {candidates.map((cand) => (
                <button
                  key={cand.site_id}
                  type="button"
                  onClick={() => { setSiteId(cand.site_id); void run(cand.site_id); }}
                  style={{
                    textAlign: 'left', fontFamily: monoFont, fontSize: 11,
                    padding: '6px 10px', background: C.surface2, color: C.text,
                    border: `1px solid ${C.border}`, borderRadius: 2, cursor: 'pointer',
                  }}
                >
                  {cand.domain} · {cand.status} · {cand.site_id}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {header !== null && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
            CURRENT RUN
          </div>
          <h2 style={{ fontFamily: serifFont, fontSize: 24, color: C.text, marginTop: 4 }}>
            {header.site_domain ?? header.site_id} · {header.protocol}
          </h2>
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2, display: 'flex', gap: 10, alignItems: 'center' }}>
            <Pill tone={statusTone(header)}>{header.status}</Pill>
            {summary !== null && (
              <span>
                {summary.pass} pass · {summary.fail} fail · {summary.n_a} n/a · {summary.duration_ms}ms
              </span>
            )}
            <span>run_id={header.run_id}</span>
            {/* ADR-0253 2026-10-27 — "Pay held order". Only shown on an
                AP2 run whose payment step escalated (visible as a step
                with step_name === 'ap2.payment_mandate' and outcome
                'pass' plus a response-body outcome 'escalated'). The
                server refuses when the ops holder key is unset or the
                account is live-mode. */}
            {header.protocol === 'ap2' && hasEscalatedPaymentStep(steps) && (
              <button
                type="button"
                onClick={() => { void payHeldOrder(); }}
                disabled={running}
                style={{
                  marginLeft: 'auto',
                  fontFamily: monoFont, fontSize: 11,
                  padding: '4px 10px',
                  background: C.lime, color: C.ink,
                  border: `1px solid ${C.border}`, borderRadius: 2,
                  cursor: running ? 'wait' : 'pointer',
                  opacity: running ? 0.6 : 1,
                }}
              >
                Pay held order
              </button>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {steps.map((step) => (
              <StepRow
                key={step.step_id}
                step={step}
                expanded={expandedStep === step.step_id}
                onToggle={() => setExpandedStep(expandedStep === step.step_id ? null : step.step_id)}
                onCopyCurl={() => void copyCurl(step)}
                copyFlash={copyFlashId === step.step_id}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
          RECENT RUNS
        </div>
        <h2 style={{ fontFamily: serifFont, fontSize: 24, color: C.text, marginTop: 4, marginBottom: 12 }}>
          Recent runs
        </h2>
        {runs.length === 0 && (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No runs yet.</div>
        )}
        {runs.map((r) => (
          <div
            key={r.run_id}
            onClick={() => void openRun(r.run_id)}
            style={{
              padding: 10, marginBottom: 6, cursor: 'pointer',
              border: `1px solid ${C.border}`, borderRadius: 2,
              background: C.surface2,
              display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: monoFont, fontSize: 11, color: C.text,
            }}
          >
            <Pill tone={statusTone(r)}>{r.status}</Pill>
            <span>{r.protocol}</span>
            <span style={{ color: C.muted }}>{r.site_domain ?? r.site_id}</span>
            {r.summary !== null && (
              <span style={{ color: C.muted }}>
                {r.summary.pass}p · {r.summary.fail}f · {r.summary.n_a}n/a
              </span>
            )}
            <span style={{ color: C.muted, marginLeft: 'auto' }}>
              {formatRelativeShort(r.started_at)} · {formatIsoCompact(r.started_at)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * ADR-0253 2026-10-27 — detect an AP2 run whose payment step reached
 * `escalated`. The step row's `fail_reason` is null on a `pass`; the
 * server captures the artifact outcome on the response_body_preview,
 * but we don't want to depend on preview-parsing here. The simplest
 * signal: the step name is `ap2.payment_mandate` and it did NOT fail
 * (escalated and approved both read as `pass` per `ap2.ts`
 * `evaluatePaymentResponse`). The "Pay held order" button is a
 * harmless click on an already-approved run (server refuses with
 * `pay_held_order_not_escalated` when the pay context row is absent).
 */
function hasEscalatedPaymentStep(steps: readonly AdminProtocolTestStep[]): boolean {
  for (const s of steps) {
    if (s.protocol === 'ap2' && s.step_name === 'ap2.payment_mandate' && s.outcome === 'pass') {
      return true;
    }
  }
  return false;
}

/**
 * Round-3 (2026-10-27) — discovery steps that are structurally static
 * reads: no MCP / A2A / UCP dispatcher involvement, no audit_record
 * emitted on the DISCOVERY_REPLICATION / MANDATE_REPLICATION queues.
 * The UI uses this to render "Not recorded in Activity by design"
 * rather than leaving a passing step with no record_id looking like
 * an invisible failure.
 */
const STATIC_DISCOVERY_STEP_NAMES: ReadonlySet<string> = new Set([
  'discovery.agent_card.edge',
  'discovery.agent_card.verify',
  'discovery.agent_card.site',
  'discovery.catalog.verify',
  'discovery.llms_txt',
  'discovery.agent_gateway',
  'discovery.robots',
]);

function isStaticDiscoveryStep(step: AdminProtocolTestStep): boolean {
  return step.protocol === 'discovery' && STATIC_DISCOVERY_STEP_NAMES.has(step.step_name);
}

function StepRow({
  step, expanded, onToggle, onCopyCurl, copyFlash,
}: {
  step: AdminProtocolTestStep;
  expanded: boolean;
  onToggle: () => void;
  onCopyCurl: () => void;
  copyFlash: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface2 }}>
      <div
        onClick={onToggle}
        style={{
          padding: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: monoFont, fontSize: 11, color: C.text,
        }}
      >
        <Pill tone={outcomeTone(step.outcome)}>{outcomeLabel(step.outcome)}</Pill>
        <span style={{ color: C.muted }}>{step.method}</span>
        <span>{step.step_name}</span>
        <span style={{ color: C.muted, marginLeft: 'auto' }}>
          {step.latency_ms !== null ? `${step.latency_ms}ms` : ''}
          {step.response_status !== null ? ` · ${step.response_status}` : ''}
          {step.record_id !== null ? ` · ${step.record_id.slice(0, 8)}…` : ''}
        </span>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: 10, fontFamily: monoFont, fontSize: 11, color: C.text }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCopyCurl(); }}
              style={{
                fontFamily: monoFont, fontSize: 11,
                padding: '4px 10px',
                background: copyFlash ? C.mint : C.surface,
                color: copyFlash ? C.ink : C.text,
                border: `1px solid ${C.border}`, borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {copyFlash ? 'Copied' : 'Copy as curl (redacted)'}
            </button>
            <span style={{ color: C.muted, fontSize: 10 }}>
              Redacted header values are placeholders. Not replayable.
            </span>
          </div>
          <div style={{ marginBottom: 6, color: C.muted }}>URL</div>
          <div style={{ wordBreak: 'break-all', marginBottom: 10 }}>{step.url}</div>
          {step.fail_reason !== null && (
            <>
              <div style={{ marginBottom: 6, color: C.muted }}>Reason</div>
              <div style={{ marginBottom: 10, color: step.outcome === 'fail' ? C.coralFg : C.text }}>{step.fail_reason}</div>
            </>
          )}
          {step.record_id !== null && (
            <>
              <div style={{ marginBottom: 6, color: C.muted }}>Audit record</div>
              <div style={{ marginBottom: 10 }}>
                {step.record_id}
                {step.inbound_protocol !== null ? ` · inbound_protocol=${step.inbound_protocol}` : ''}
              </div>
            </>
          )}
          {step.record_id === null && step.outcome === 'pass' && isStaticDiscoveryStep(step) && (
            // Round-3 (2026-10-27) — the discovery probes are static
            // reads of /.well-known/*, /llms.txt, /agent, /robots.txt
            // and the edge-served signed card / catalog. None of them
            // produce an `audit_record` by design (they do not pass
            // through the MCP / A2A / UCP dispatcher; the edge
            // card/catalog handlers do not write to DISCOVERY_REPLICATION).
            // Say so plainly rather than let the operator wonder why
            // no record appeared.
            <>
              <div style={{ marginBottom: 6, color: C.muted }}>Audit record</div>
              <div style={{ marginBottom: 10, color: C.muted }}>
                Not recorded in Activity by design — static discovery read.
              </div>
            </>
          )}
          {step.request_headers_json !== null && Object.keys(step.request_headers_json).length > 0 && (
            <>
              <div style={{ marginBottom: 6, color: C.muted }}>Request headers (redacted)</div>
              <pre style={{ overflow: 'auto', maxHeight: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: C.surface, color: C.text, padding: 8, borderRadius: 2 }}>
{JSON.stringify(step.request_headers_json, null, 2)}
              </pre>
            </>
          )}
          {step.request_body_preview !== null && (
            <>
              <div style={{ marginTop: 10, marginBottom: 6, color: C.muted }}>Request body</div>
              <pre style={{ overflow: 'auto', maxHeight: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: C.surface, color: C.text, padding: 8, borderRadius: 2 }}>
{step.request_body_preview}
              </pre>
            </>
          )}
          {step.response_headers_json !== null && (
            <>
              <div style={{ marginTop: 10, marginBottom: 6, color: C.muted }}>Response headers</div>
              <pre style={{ overflow: 'auto', maxHeight: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: C.surface, color: C.text, padding: 8, borderRadius: 2 }}>
{JSON.stringify(step.response_headers_json, null, 2)}
              </pre>
            </>
          )}
          {step.response_body_preview !== null && (
            <>
              <div style={{ marginTop: 10, marginBottom: 6, color: C.muted }}>Response body (bounded, truncation sentinel)</div>
              <pre style={{ overflow: 'auto', maxHeight: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: C.surface, color: C.text, padding: 8, borderRadius: 2 }}>
{step.response_body_preview}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
